import type { PageContext } from '@zto/zpage'
import dayjs from 'dayjs'
import { YN, SiteStatus } from '@/config/enums'
import { commonOptions } from '@/config/options'

export function addEditDialog(isEdit: boolean) {
  return {
    width: 820,
    labelWidth: 120,
    itemSpan: 24,
    title: isEdit ? '编辑站点' : '新建站点',
    onShow: async ({ app, data }: PageContext) => {
      if (!isEdit) {
        return {
          payload: { status: SiteStatus.ENABLED, communityCodes: [], siteUserList: [] }
        }
      }
      const siteCode = data?.siteCode
      const detail = await app.request({
        api: '/sys/admin/site/detail',
        method: 'POST',
        params: {
          siteCode,
          leader: YN.NO
        }
      })
      const { provinceCode, province, cityCode, city, districtCode, district, siteCommunityList, ...other } =
        detail || {}
      return {
        payload: {
          ...other,
          pcd: [{ provinceCode, province, cityCode, city, districtCode, district }],
          communityCodes: siteCommunityList?.map((it: any) => it?.communityCode).filter(Boolean),
          communityNames: siteCommunityList?.map((it: any) => it?.communityName).filter(Boolean)
        }
      }
    },
    formItems: [
      { type: 'text', prop: 'siteCode', label: '站点编号', visibleOn: `${isEdit}` },
      {
        type: 'input',
        prop: 'siteName',
        label: '站点名称',
        required: true,
        trim: true,
        showWordLimit: true,
        maxlength: 50
      },
      {
        type: 'checkbox',
        checkType: 'radio',
        prop: 'status',
        label: '站点状态',
        options: commonOptions.siteStatus,
        default: SiteStatus.ENABLED,
        required: true,
        span: 12
      },
      {
        type: 'html',
        label: '',
        labelWidth: 0,
        span: 12,
        html: '<span style="color: #606266;">禁用后不可发起新的配送任务，其余不受影响</span>'
      },
      {
        type: 'fuzzy-select',
        prop: 'communityCodes',
        label: '关联小区',
        optionValueProp: 'communityCode',
        optionLabelProp: 'communityName',
        triggerFocus: true,
        labelProp: 'communityNames',
        multiple: true,
        multipleLimit: 10,
        autoHeight: true,
        collapseTags: false,
        // collapseTagsTooltip: true,
        // maxCollapseTags: 2,
        remote: true,
        keywordProp: 'communityName',
        api: '/sys/admin/api/region/community/pageList',
        required: true
      },
      {
        label: '所在地区',
        type: 'area-pro',
        prop: 'pcd',
        keyConfig: {
          provinceCode: 'provinceCode',
          province: 'province',
          cityCode: 'cityCode',
          city: 'city',
          districtCode: 'districtCode',
          district: 'district'
        },
        filterable: true,
        required: true
      },
      {
        type: 'input',
        prop: 'addressDetail',
        label: '详细地址',
        required: true,
        trim: true,
        showWordLimit: true,
        maxlength: 120
      },
      { type: 'input', prop: 'leaderName', label: '负责人姓名', required: true, trim: true, maxlength: 10 },
      {
        type: 'input',
        prop: 'leaderPhone',
        label: '负责人手机',
        required: true,
        trim: true,
        maxlength: 11,
        rules: [{ pattern: /^1[3-9][0-9]\d{8}$/, message: '请填写正确的手机号码' }]
      },
      {
        type: 'html',
        label: '站点人员',
        span: 10,
        html: '<span style="color: #606266;">添加成员手机号与姓名即可</span>'
      },
      {
        type: 'action',
        actionLabel: '添加人员',
        trigger: async (allAttrs: any, ctx: any) => {
          const { Message } = ctx.app.useMessage()
          // if (!ctx.data.directYear) {
          //   Message.warning('请输入直投年限')
          //   return false
          // }

          // 添加
          ctx.data.siteUserList.unshift({ realName: '', phone: '' })
        },
        visibleOn: ({ data }: PageContext) => {
          return !data?.siteUserList?.length
        },
        span: 14,
        labelWidth: 0,
        fullWidth: false
      },
      {
        type: 'action',
        actionLabel: '清空人员',
        trigger: async (allAttrs: any, ctx: any) => {
          const { Message } = ctx.app.useMessage()
          // if (!ctx.data.directYear) {
          //   Message.warning('请输入直投年限')
          //   return false
          // }

          // 添加
          ctx.data.siteUserList = []
        },
        buttonType: 'danger',
        visibleOn: ({ data }: PageContext) => {
          return !!data?.siteUserList?.length
        },
        span: 14,
        labelWidth: 0,
        fullWidth: false
      },
      {
        label: '',
        labelWidth: 0,
        // disabled: true,
        deletable: true,
        type: 'edit-table',
        prop: 'siteUserList',
        span: 24,
        autoHeight: true,
        border: true,
        autoAppendFirst: false,
        maxRows: 10,
        dynamicAttrs: () => {
          return {
            columns: [
              {
                label: '姓名',
                prop: 'realName',
                minWidth: 120,
                editor: {
                  itemType: 'input',
                  label: '',
                  maxlength: 10,
                  trim: true,
                  rules: [{ required: true, message: '请输入姓名', trigger: 'blur' }]
                }
              },
              {
                label: '手机号',
                prop: 'phone',
                minWidth: 140,
                editor: {
                  itemType: 'input',
                  label: '',
                  maxlength: 11,
                  rules: [
                    {
                      required: true,
                      validator: async (rule: any, value: any, callback: any) => {
                        if (!value) {
                          return callback(new Error('请填写手机号码'))
                        }
                        if (!/^1[3-9][0-9]\d{8}$/.test(value)) {
                          return callback(new Error('请填写正确的手机号码'))
                        }
                        return callback()
                      },
                      trigger: 'blur'
                    }
                  ]
                }
              }
            ]
          }
        }
      }
    ],
    beforeSubmit: async ({ data }: PageContext, model: any, options: any) => {
      const { pcd, siteUserList, communityCodes, communityNames, ...other } = data
      const siteUsers = siteUserList.map((item: any) => {
        return {
          ...item,
          leader: YN.NO
        }
      })
      siteUsers.unshift({
        realName: data.leaderName,
        phone: data.leaderPhone,
        leader: YN.YES
      })
      const payload = {
        ...other,
        ...(pcd?.[0] || {}),
        siteUserList: siteUsers,
        siteCommunityList: communityCodes.map((item: string) => ({ communityCode: item }))
      }
      console.info('🚀 ~ file:_dialogs method:beforeSubmit line:268 ----- payload', payload)
      return payload
    },
    actions: {
      close: {},
      submit: { label: isEdit ? '保存' : '确定' }
    }
  }
}

export function logDialog() {
  return {
    title: `操作日志`,

    width: 800,
    labelWidth: 0,
    itemSpan: 24,
    noPadding: true,

    bodyHeight: '400px',

    actions: [{ name: 'close', label: '关闭' }],

    body: ({ data }: PageContext) => {
      return {
        type: 'crud',
        actions: {
          query: {
            api: '/sys/admin/sys-config/queryOptLogList',
            apiParams: { snCode: data.siteCode, module: 'SITE' }
          }
        },
        // header: {
        //   body: {
        //     type: 'html',
        //     html: `<div class="q-py-md" style="font-size: 12px;">设备${data.snCode}在平台上的关键操作记录（含添加、编辑、召回、重启、停止等）。</div>`
        //   }
        // },
        toolbar: { hidden: true },
        search: { hidden: true },
        table: {
          columns: [
            { label: '时间', prop: 'gmtCreate', width: 150 },
            // { label: '操作类型', prop: 'optType', width: 100 },
            { label: '操作人', prop: 'operatorName', width: 100 },
            // { label: '结果', prop: 'afterStatus', width: 80 },
            { label: '操作内容', prop: 'content', minWidth: 150 }
          ]
        }
      }
    }
  }
}
